import { useContext, useEffect, useState } from 'react';
import {
    Dropdown,
    IDropdownListItem,
} from '@acko-tech/building-blocks.ui.dropdown';
import SearchInput from '@acko-tech/building-blocks.ui.search-input';
import {
    AckoMainTheme,
    ThemeProvider,
} from '@acko-tech/building-blocks.ui.theme';
import { useRouter } from 'next/router';
import { NewPlanStages } from '../constants';
import { MPosContext } from 'utils/context';

// constants
import {
    SegmentEventNameTypes,
    SegmentEventPageTypes,
    SegmentEventPages,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    SegmentEventFieldTypes,
} from 'utils/segment/constants';
import sendSegmentEvent from 'utils/segment';
import { Permissions, PurchaseJourney } from 'utils/constants';

import { useViewport } from 'utils/responsive';
import { IOption } from './types';
import SnackBar from 'library/snackbar';
import { getLabels } from './utils';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import * as Styles from './styles';

// components
import VehicleValidationScreen from './vehicleValidationScreen/VehicleValidationScreen';

function MobileModel() {
    const router = useRouter();

    const {
        planDetails,
        setActiveStage,
        setPlanDetails,
        permissions,
        userDetails,
        permissionsMap,
    } = useContext(MPosContext);

    const [options, setOptions] = useState<IOption[]>([]);
    const [modelOptions, setModelOptions] = useState<IDropdownListItem[]>([]);
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedModelOption, setSelectedModelOption] = useState({
        value: planDetails?.device?.id || '',
        displayValue: planDetails?.device?.name || '',
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const { isMobile } = useViewport();
    const purchaseJourney = permissions?.purchase_journey;
    const showManualFlowForV1 =
        purchaseJourney === PurchaseJourney.V1 && planDetails?.showManualFlow;

    /**
     * view segment hook
     */

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const view_name =
                purchaseJourney === PurchaseJourney.V0 || showManualFlowForV1
                    ? SegmentEventPages.VIEW_DEVICE_MODEL
                    : SegmentEventPages.VIEW_VEHICLE_VALIDATION_PAGE;

            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPageTypes.VEHICLE_VALIDATION_PAGE,
                    view_name,
                },
                {
                    userDetails,
                    isMobile,
                }
            );
        }
    }, [window, showManualFlowForV1, purchaseJourney]);

    useEffect(() => {
        setSelectedModelOption({
            value: planDetails?.device?.id || '',
            displayValue: planDetails?.device?.name || '',
        });
    }, [planDetails]);

    useEffect(() => {
        if (showManualFlowForV1 || purchaseJourney === PurchaseJourney.V0) {
            try {
                import('../apis').then(async (req) => {
                    const { models, statusCode } = await req.getMobileModels(
                        permissions.partner_id,
                        permissions.group
                    );
                    setIsLoading(false);
                    if (statusCode === 401) {
                        router.push('/login');
                        setSnackbar({
                            type: 'failure',
                            message:
                                'Token expired. Redirecting to login page.',
                        });
                        return;
                    }
                    if (models.length > 0) {
                        setOptions(models);
                        const options = models.map(({ id, name }) => {
                            return {
                                displayValue: name,
                                value: id,
                            };
                        });
                        setModelOptions(options);
                    }
                });
            } catch {}
        } else {
            setIsLoading(false);
        }
    }, [showManualFlowForV1, purchaseJourney]);

    useEffect(() => {
        if (options.length === 0) return;
        if (search.length > 0) {
            const filteredList = options.filter((option) =>
                option.name.toLowerCase().includes(search.toLowerCase())
            );
            const opts = filteredList.map(({ id, name }) => {
                return {
                    displayValue: name,
                    value: id,
                };
            });
            setModelOptions(opts);
        } else {
            const opts = options.map(({ id, name }) => {
                return {
                    displayValue: name,
                    value: id,
                };
            });
            setModelOptions(opts);
        }
    }, [search]);

    const toggleDropdown = (show: boolean) => {
        setShowDropdown(show);
        if (!show) {
            const opts = options.map(({ id, name }) => {
                return {
                    displayValue: name,
                    value: id,
                };
            });
            setModelOptions(opts);
        }
    };

    const onSelectModelDropdown = ({
        value,
        displayValue,
    }: IDropdownListItem) => {
        try {
            setSelectedModelOption({
                value,
                displayValue,
            });
            const selectedModel = options.filter(({ id }) => value === id);
            if (!(selectedModel?.length > 0)) {
                return;
            }
            setPlanDetails({
                ...planDetails,
                device: {
                    ...planDetails.device,
                    name: displayValue,
                    id: value,
                    type: selectedModel[0].type,
                    price: selectedModel[0].price,
                },
            });
            setActiveStage(NewPlanStages.PLAN);

            // segment Event
            sendSegmentEvent(
                SegmentEventNameTypes.ACTION,
                {
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActionNames.SELECT_MODEL,
                    button_name: `select_${displayValue}_model`,
                    field_selected: SegmentEventFieldTypes.DROPDOWN,
                },
                { userDetails, isMobile }
            );
        } catch {}
    };

    const { label2, placeholder } = getLabels(
        permissions?.partner_id,
        purchaseJourney
    );

    return (
        <ThemeProvider theme={AckoMainTheme}>
            <>
                {isLoading ? (
                    <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
                ) : (
                    <>
                        {purchaseJourney === PurchaseJourney.V1 && (
                            <VehicleValidationScreen
                                setSelectedModelOption={setSelectedModelOption}
                            />
                        )}

                        {permissionsMap[Permissions.MANUAL_PURCHASE_FLOW] && (
                            <>
                                {(purchaseJourney === PurchaseJourney.V0 ||
                                    showManualFlowForV1) && (
                                    <>
                                        <Styles.SelectModelText>
                                            {label2}
                                        </Styles.SelectModelText>
                                        <Styles.SelectModelDropdown>
                                            <Dropdown
                                                show={showDropdown}
                                                value={selectedModelOption}
                                                label={placeholder}
                                                options={modelOptions}
                                                onChange={onSelectModelDropdown}
                                                toggleDropdown={toggleDropdown}
                                            >
                                                <div
                                                    style={{
                                                        marginBottom: '10px',
                                                    }}
                                                >
                                                    <SearchInput
                                                        placeholder={
                                                            placeholder
                                                        }
                                                        handleChange={(value) =>
                                                            setSearch(value)
                                                        }
                                                    />
                                                </div>
                                            </Dropdown>
                                        </Styles.SelectModelDropdown>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
                {/* <PopularModels onClick={onSelectModel} /> */}
                <SnackBar
                    {...snackbar}
                    handleSnakbarComplete={() =>
                        setSnackbar({ type: '', message: '' })
                    }
                />
            </>
        </ThemeProvider>
    );
}

export default MobileModel;
